import React, {
  useContext,
  type FC,
  type MouseEvent,
  type FocusEvent,
  useEffect,
  useRef,
  type PropsWithChildren,
} from 'react';

import { theme, variant } from '@lucid/theme';
import { type Lucid } from '@lucid/utils';
import gsap from 'gsap';
import styled from '@emotion/styled';

import Box from '../box/box';
import Link from '../link/link';
import Nav, { SUPER_NAV_HEIGHT, SITE_NAV_HEIGHT, type SharedNavOptions } from './nav';
import Text from '../text/text';
import NavDropdownMenuGroup from './nav-dropdown-menu-group';

export interface NavDropdownMenuProps extends SharedNavOptions {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if the dropdown should be visible or hidden. */
  isDropdownOpen?: boolean;
  /** The link and nested links to be shown in the dropdown menu. */
  link: Lucid.Directus.Generated.Nav_Section_Translations;
  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
}

const StyledTextContent = styled(Text)(
  variant({
    scale: 'nav.textContent',
    prop: 'type',
  }),
);

const NavDropdownMenu: FC<PropsWithChildren<NavDropdownMenuProps>> = ({
  className,
  isDropdownOpen,
  isSuperNav = false,
  link,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  ...rest
}) => {
  const { viewMoreText } = useContext(Nav.Context);
  const viewMoreLink = link?.link;

  const handleOnMouseEnter = (e: MouseEvent) => {
    if (onMouseEnter) {
      return onMouseEnter(e);
    }
  };

  const handleOnMouseLeave = (e: MouseEvent) => {
    if (onMouseLeave) {
      return onMouseLeave(e);
    }
  };

  const handleOnBlur = (e: FocusEvent) => {
    if (onBlur && !e.currentTarget.contains(e.relatedTarget as Node)) {
      // does not blur if focus stays within the same dropdown
      return onBlur(e);
    }
  };

  const handleOnFocus = (e: FocusEvent) => {
    if (onFocus) {
      return onFocus(e);
    }
  };

  const animationRef = useRef<HTMLDivElement>(null);
  const menuLinksRef = useRef<HTMLDivElement>(null);
  const featuredItemRef = useRef<HTMLDivElement>(null);
  const containerQ = gsap.utils.selector(animationRef);
  const menuQ = gsap.utils.selector(menuLinksRef);
  const featuredQ = featuredItemRef.current ? gsap.utils.selector(featuredItemRef) : () => null;
  gsap.config({ nullTargetWarn: false });

  useEffect(() => {
    if (animationRef.current) {
      isDropdownOpen
        ? gsap
            .timeline()
            .to(
              menuQ('a'),
              {
                opacity: 1,
                duration: 0.1,
                stagger: { each: 0.025 },
                ease: theme.beziers.slowStartEase,
              },
              '0',
            )
            .to(
              containerQ('[aria-level="3"]'),
              {
                opacity: 1,
                duration: 0.5,
                overwrite: true,
              },
              '0',
            )
            .to(
              menuQ('a'),
              {
                y: 0,
                duration: 0.157,
                stagger: { each: 0.025 },
                ease: theme.beziers.slowStartEase,
              },
              '0',
            )
            .to(
              featuredItemRef.current,
              {
                opacity: 1,
                duration: 0.25,
              },
              '0.2',
            )
            .to(
              featuredQ('p'),
              {
                x: 0,
                duration: 0.3,
              },
              '0.2',
            )
        : gsap
            .timeline()
            .set(featuredItemRef.current, {
              opacity: 0,
              overwrite: true,
            })
            .set(containerQ('[aria-level="3"]'), {
              opacity: 0,
              overwrite: true,
            })
            .set(menuQ('a'), {
              opacity: 0,
              y: '-16px',
              overwrite: true,
            })
            .set(featuredQ('p'), {
              x: '-16px',
              overwrite: true,
            });
    }
  }, [isDropdownOpen]);

  return (
    <Box
      ref={animationRef}
      {...rest}
      aria-hidden={!isDropdownOpen}
      css={theme => [
        {
          label: 'navDropdownMenu',
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          backgroundColor: theme.colors.white,
          height: '100%',
          maxHeight: 0,
          width: '100%',
          overflow: 'hidden',
          [theme.breakpoints.bp768.min]: {
            flexDirection: 'row',
            flexWrap: 'wrap',
          },
          [theme.breakpoints.bp1024.min]: {
            position: 'absolute',
            top: isSuperNav ? SUPER_NAV_HEIGHT : SITE_NAV_HEIGHT,
            padding: 0,
            flexDirection: 'row',
            flexWrap: 'nowrap',
            height: 'auto',
            left: 0,
            margin: '0 1.875rem',
            width: 'fit-content',
          },
          [theme.breakpoints.bp1220.min]: {
            width: 'fit-content',
            left: '15%',
          },
        },
        isDropdownOpen && {
          zIndex: theme.zIndices.z100,
          maxHeight: '1000px',
          [theme.breakpoints.bp1024.min]: {
            marginTop: 'unset',
            paddingBottom: 'unset',
            boxShadow: theme.shadows.nav,
            borderBottomLeftRadius: theme.radii.r4,
            borderBottomRightRadius: theme.radii.r4,
          },
        },
      ]}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    >
      <StyledTextContent
        css={(theme: Lucid.Theme.Brand) => ({
          label: 'navDropdownMenuTextContent',
          display: 'none',
          visibility: 'hidden',
          position: 'relative',
          [theme.breakpoints.bp1024.min]: {
            display: 'flex',
            visibility: 'visible',
            flexDirection: 'column',
            flex: '0 1 100%',
            width: '250px',
            padding: theme.space.s24,
            color: theme.colors.white,
          },
        })}
        type={isSuperNav ? 'superNav' : 'siteNav'}
      >
        <Text as="span" dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'} variant="paragraphL400">
          {link.dropdown_text}
        </Text>
      </StyledTextContent>
      <Box
        css={theme => [
          {
            padding: `${theme.space.s16}px ${theme.space.s32}px 0`,
            width: '100%',
            position: 'relative',
            alignSelf: 'flex-start',
            [theme.breakpoints.bp1024.min]: {
              padding: theme.space.s24,
            },
            [theme.breakpoints.bp1220.min]: {
              width: 'unset',
            },
          },
          isDropdownOpen && {
            zIndex: theme.zIndices.z500,
          },
        ]}
      >
        <Box
          ref={menuLinksRef}
          css={theme => [
            {
              label: 'navDropdownMenuLinks',
              [theme.breakpoints.bp1024.min]: {
                display: 'flex',
                gap: theme.space.s24,
              },
            },
          ]}
        >
          {link?.dropdown_sections?.map((itemLink, itemLinkIndex) => (
            <NavDropdownMenuGroup
              key={`navDropdownMenuGroup-${link?.id}-${itemLinkIndex + 1}`}
              css={theme =>
                viewMoreLink &&
                itemLinkIndex + 1 === link?.dropdown_sections?.length && {
                  '& > ul:last-of-type': { paddingBottom: theme.space.s32 },
                }
              }
              index={itemLinkIndex}
              isDropdownOpen={isDropdownOpen}
              isSuperNav={isSuperNav}
              link={itemLink}
            />
          ))}
        </Box>
        {viewMoreLink && (
          <Box
            css={theme => [
              {
                textAlign: 'right',
                borderTop: `1px solid ${theme.colors.coolGrey[10]}`,
                padding: `${theme.space.s24}px ${theme.space.s16}px`,
                [theme.breakpoints.bp1024.min]: {
                  padding: `${theme.space.s24}px ${theme.space.s16}px ${theme.space.s8}px`,
                },
              },
              isSuperNav && {
                '& > a': {
                  color: theme.colors.blue[60],
                },
              },
            ]}
          >
            <Link iconAnimation="navigational" tabIndex={isDropdownOpen ? 0 : -1} to={viewMoreLink.final_url}>
              {viewMoreLink.default_link_text || viewMoreText}
            </Link>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { NavDropdownMenu };
export default NavDropdownMenu;
